import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {
    CustomerAgreement,
    CustomerAgreementInput,
    CustomerAgreementLanguage,
    CustomerAgreementListItem
} from './types';
import { CustomerAgreementsList } from './view/CustomerAgreementsList';
import { CustomerAgreementDialog } from './view/CustomerAgreementDialog';
import {
    CustomerAgreementDialogState,
    useCustomerAgreementDialog
} from './state/useCustomerAgreementDialog';
import {
    SaveCustomerAgreementFailure,
    SaveCustomerAgreementSuccess,
    useSaveCustomerAgreement
} from './state/useSaveCustomerAgreement';
import { saveErrorText } from './state/saveErrorText';
import { useCustomerAgreementsQuery } from './queries/useCustomerAgreementsQuery';
import { useCustomerAgreementLanguagesQuery } from './queries/useCustomerAgreementLanguagesQuery';
import { useCustomerAgreementQuery } from './queries/useCustomerAgreementQuery';
import { Translate } from '../../shared/LanguageService';
import * as Features from '../../shared/Features';
import CountryService from '../../shared/CountryService';
import { preferredAgreementLanguage } from './preferredAgreementLanguage';
import { useUserContext } from '../../shared/context/user-context';
import { useTelemetryContext } from '../../telemetry/telemetryContext';
import { getUserInfo } from '../../telemetry/utils';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';
import WarningSnackbar from '../../shared/feedback/WarningSnackbar';
import { customersFallingBackText } from './state/customersFallingBackText';
import { useConfirmedSave } from './state/useConfirmedSave';
import { ConfirmDeactivationDialog } from './view/ConfirmDeactivationDialog';
import { AffectedCustomer } from './types';

type CustomerAgreementsPageProps = {
    customerAgreements: CustomerAgreementListItem[];
    loadFailed: boolean;
    canCreate: boolean;
    canEdit: boolean;
    languages: CustomerAgreementLanguage[];
    preferredLanguage: string;
    dialog: CustomerAgreementDialogState;
    customerAgreementToEdit: CustomerAgreement | undefined;
    saving: boolean;
    onCreate: () => void;
    onEdit: (customerAgreement: CustomerAgreementListItem) => void;
    onCloseDialog: () => void;
    onSave: (input: CustomerAgreementInput & { isActive?: boolean }) => void;
    saveError: SaveCustomerAgreementFailure | null;
    onDismissSaveError: () => void;
    saveSuccess: SaveCustomerAgreementSuccess | null;
    onDismissSaveSuccess: () => void;
    customersAffectedByDeactivation: AffectedCustomer[] | null;
    onConfirmDeactivation: () => void;
    onCancelDeactivation: () => void;
    deactivationCheckFailed: boolean;
    onDismissDeactivationCheckFailed: () => void;
};

function successText(saveSuccess: SaveCustomerAgreementSuccess | null): string {
    if (saveSuccess === null) {
        return '';
    }

    return Translate(
        saveSuccess.created
            ? 'customer-agreements/notification/create-succeeded'
            : 'customer-agreements/notification/update-succeeded',
        { name: saveSuccess.name }
    );
}

export function CustomerAgreementsPage({
    customerAgreements,
    loadFailed,
    canCreate,
    canEdit,
    languages,
    preferredLanguage,
    dialog,
    customerAgreementToEdit,
    saving,
    onCreate,
    onEdit,
    onCloseDialog,
    onSave,
    saveError,
    onDismissSaveError,
    saveSuccess,
    onDismissSaveSuccess,
    customersAffectedByDeactivation,
    onConfirmDeactivation,
    onCancelDeactivation,
    deactivationCheckFailed,
    onDismissDeactivationCheckFailed
}: CustomerAgreementsPageProps) {
    const showCreateDialog = dialog.mode === 'create' && languages.length > 0;
    const showEditDialog =
        dialog.mode === 'edit' && customerAgreementToEdit !== undefined && languages.length > 0;

    return (
        <section>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1>{Translate('customer-agreements/list/heading')}</h1>
                {canCreate ? (
                    <Button variant="contained" onClick={onCreate}>
                        {Translate('customer-agreements/list/button/add')}
                    </Button>
                ) : null}
            </Box>
            {loadFailed ? (
                <Alert severity="error" variant="filled">
                    {Translate('customer-agreements/list/load-error')}
                </Alert>
            ) : (
                <CustomerAgreementsList
                    customerAgreements={customerAgreements}
                    canEdit={canEdit}
                    onEdit={onEdit}
                />
            )}
            {showCreateDialog ? (
                <CustomerAgreementDialog
                    open={true}
                    title="customer-agreements/modal/title-create"
                    languages={languages}
                    preferredLanguage={preferredLanguage}
                    initial={null}
                    saving={saving}
                    onSave={onSave}
                    onClose={onCloseDialog}
                />
            ) : null}
            {showEditDialog ? (
                <CustomerAgreementDialog
                    open={true}
                    title="customer-agreements/modal/title-edit"
                    languages={languages}
                    preferredLanguage={preferredLanguage}
                    initial={customerAgreementToEdit}
                    saving={saving}
                    onSave={onSave}
                    onClose={onCloseDialog}
                />
            ) : null}
            <FailureSnackbar
                open={saveError !== null}
                onClose={onDismissSaveError}
                text={saveErrorText(saveError)}
            />
            <ConfirmDeactivationDialog
                open={customersAffectedByDeactivation !== null}
                customers={customersAffectedByDeactivation || []}
                onConfirm={onConfirmDeactivation}
                onCancel={onCancelDeactivation}
            />
            <FailureSnackbar
                open={deactivationCheckFailed}
                onClose={onDismissDeactivationCheckFailed}
                stackIndex={1}
                text={Translate('customer-agreements/confirm-deactivation/check-failed')}
            />
            <SuccessSnackbar
                open={saveSuccess !== null && saveSuccess.customersFallingBack === null}
                onClose={onDismissSaveSuccess}
                text={successText(saveSuccess)}
            />
            <WarningSnackbar
                open={saveSuccess !== null && saveSuccess.customersFallingBack !== null}
                onClose={onDismissSaveSuccess}
                text={
                    saveSuccess !== null && saveSuccess.customersFallingBack !== null
                        ? customersFallingBackText(saveSuccess.customersFallingBack)
                        : ''
                }
            />
        </section>
    );
}

export default function CustomerAgreementsPageDataLayer() {
    const { user } = useUserContext();
    const { logger } = useTelemetryContext();
    const { dialog, openCreateDialog, openEditDialog, closeDialog } = useCustomerAgreementDialog();
    const { saveCustomerAgreement, saveError, dismissSaveError, saveSuccess, dismissSaveSuccess } =
        useSaveCustomerAgreement();

    const customerAgreementsQuery = useCustomerAgreementsQuery({
        onError: (error) => {
            logger.logException(error, {
                origin: 'CustomerAgreementsPage.DataLayer',
                userInfo: getUserInfo(user)
            });
        }
    });
    const languagesQuery = useCustomerAgreementLanguagesQuery();
    const customerAgreementToEdit = dialog.mode === 'edit' ? dialog.customerAgreement : null;
    const textsQuery = useCustomerAgreementQuery(
        customerAgreementToEdit ? customerAgreementToEdit.id : null
    );

    const {
        requestSave,
        pendingDeactivation,
        saving,
        confirmDeactivation,
        cancelDeactivation,
        checkFailed,
        dismissCheckFailed
    } = useConfirmedSave({ save: saveCustomerAgreement, closeEditor: closeDialog });

    function handleSave(input: CustomerAgreementInput & { isActive?: boolean }) {
        requestSave(
            customerAgreementToEdit ? customerAgreementToEdit.id : null,
            Boolean(customerAgreementToEdit && customerAgreementToEdit.isActive),
            input
        );
    }

    return (
        <CustomerAgreementsPage
            customerAgreements={customerAgreementsQuery.data || []}
            loadFailed={customerAgreementsQuery.isError}
            canCreate={Boolean(Features.hasFeature(Features.CreateCustomerAgreements, user))}
            canEdit={Boolean(Features.hasFeature(Features.EditCustomerAgreements, user))}
            languages={languagesQuery.data || []}
            preferredLanguage={preferredAgreementLanguage(
                CountryService.getDefaultOrImpersonatedCountryId(),
                languagesQuery.data || []
            )}
            dialog={dialog}
            customerAgreementToEdit={
                customerAgreementToEdit && textsQuery.data
                    ? { ...customerAgreementToEdit, texts: textsQuery.data.texts }
                    : undefined
            }
            saving={saving}
            onCreate={openCreateDialog}
            onEdit={openEditDialog}
            onCloseDialog={closeDialog}
            onSave={handleSave}
            saveError={saveError}
            onDismissSaveError={dismissSaveError}
            saveSuccess={saveSuccess}
            onDismissSaveSuccess={dismissSaveSuccess}
            customersAffectedByDeactivation={
                pendingDeactivation ? pendingDeactivation.customers : null
            }
            onConfirmDeactivation={confirmDeactivation}
            onCancelDeactivation={cancelDeactivation}
            deactivationCheckFailed={checkFailed}
            onDismissDeactivationCheckFailed={dismissCheckFailed}
        />
    );
}
